<script>
  import { currentScreen, availableTeams, matchSetup } from '../store.js';
  import { clearActiveMatch } from '../../store.js';
  import { toast, confirmDialog } from '../../ui/dialogs.js';
  import { onMount } from 'svelte';
  import { sortTournamentStandings, generateKnockoutFixtures } from '../../tournament-bracket-engine.js';
  import KnockoutBracket from '../components/KnockoutBracket.svelte';

  let tournaments = [];
  let activeTournament = null;
  let view = 'list'; // 'list', 'create', 'detail'

  // Tournament Form
  let name = "";
  let selectedTeams = [];
  let overs = 10;
  let maxOversPerBowler = 2;
  let wideValue = 1;
  let noBallValue = 1;
  let rebowlWides = true;
  let rebowlNoBalls = true;

  onMount(() => {
    loadTournaments();
  });

  function loadTournaments() {
    try {
      tournaments = JSON.parse(localStorage.getItem('cs_tournaments_v1') || "[]");
    } catch (_) {
      tournaments = [];
    }
  }

  function goBack() {
    detailTab = 'table';
    if (view === 'detail') {
      view = 'list';
      activeTournament = null;
    } else if (view === 'create') {
      view = 'list';
    } else {
      currentScreen.set('home');
    }
  }

  function toggleTeamSelection(teamName) {
    if (selectedTeams.includes(teamName)) {
      selectedTeams = selectedTeams.filter(t => t !== teamName);
    } else {
      selectedTeams = [...selectedTeams, teamName];
    }
  }

  function generateRoundRobin(teamsList) {
    const fixtures = [];
    const n = teamsList.length;
    let index = 1;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        fixtures.push({
          id: "tmatch_" + index + "_" + Date.now(),
          teamA: teamsList[i],
          teamB: teamsList[j],
          played: false,
          matchId: null,
          winner: null,
          margin: "",
          marginType: ""
        });
        index++;
      }
    }
    return fixtures;
  }

  function createTournament() {
    if (!name.trim()) {
      toast("Please enter a tournament name.", "error");
      return;
    }
    if (selectedTeams.length < 2) {
      toast("Select at least 2 teams.", "error");
      return;
    }

    const fixtures = generateRoundRobin(selectedTeams);
    const ptsTable = {};
    selectedTeams.forEach(tName => {
      ptsTable[tName] = {
        played: 0, won: 0, lost: 0, tied: 0, pts: 0,
        runsScored: 0, ballsFaced: 0, runsConceded: 0, ballsBowled: 0,
        nrr: 0.00
      };
    });

    const newT = {
      id: "tour_" + Date.now(),
      name,
      teams: selectedTeams,
      overs,
      rules: {
        maxOversPerBowler,
        wideValue,
        noBallValue,
        rebowlWides,
        rebowlNoBalls
      },
      fixtures,
      pointsTable: ptsTable,
      createdAt: Date.now()
    };

    tournaments = [...tournaments, newT];
    localStorage.setItem('cs_tournaments_v1', JSON.stringify(tournaments));
    
    // Reset form
    name = "";
    selectedTeams = [];
    view = 'list';
    toast("🏆 Tournament created successfully!", "success");
  }

  function selectTournament(t) {
    activeTournament = t;
    view = 'detail';
  }

  async function deleteTournament(id) {
    if (await confirmDialog("Delete this tournament permanently? All fixtures and stats will be deleted.", { title: "Delete tournament", danger: true })) {
      tournaments = tournaments.filter(t => t.id !== id);
      localStorage.setItem('cs_tournaments_v1', JSON.stringify(tournaments));
      if (activeTournament?.id === id) activeTournament = null;
    }
  }

  async function playFixture(fixture) {
    if (await confirmDialog(`Start match between ${fixture.teamA} and ${fixture.teamB}?`, { title: "Start fixture" })) {
      // Find full team objects
      const tA = $availableTeams.find(t => t.name === fixture.teamA);
      const tB = $availableTeams.find(t => t.name === fixture.teamB);

      if (!tA || !tB) {
        toast("Teams not found in participating team list!", "error");
        return;
      }

      // Initialize match setup store
      matchSetup.set({
        teamA: tA,
        teamB: tB,
        overs: activeTournament.overs,
        mode: "full",
        lms: true,
        tossWinner: "A",
        tossDecision: "bat",
        rules: activeTournament.rules,
        tournamentId: activeTournament.id,
        tournamentMatchId: fixture.id
      });

      await clearActiveMatch();
      currentScreen.set('setup');
    }
  }

  // Points Table Sorting: PTS (desc), WINS (desc), NRR (desc), Runs (desc)
  // Fixtures are needed for the head-to-head tie-break, which outranks NRR
  // whenever two teams finish level on points (IPL/ICC rule).
  $: sortedPoints = activeTournament && activeTournament.pointsTable
    ? sortTournamentStandings(activeTournament.pointsTable, activeTournament.fixtures || [])
    : [];
  $: playoffFixtures = activeTournament ? generateKnockoutFixtures(sortedPoints) : [];

  let detailTab = 'table'; // 'table', 'bracket', 'fixtures', 'stats'

  $: playerStatsList = activeTournament && activeTournament.playerStats ? Object.values(activeTournament.playerStats) : [];
  $: orangeCap = [...playerStatsList].sort((a, b) => b.runs - a.runs || a.balls - b.balls).slice(0, 5);
  $: purpleCap = [...playerStatsList].sort((a, b) => b.wickets - a.wickets || a.runsConceded - b.runsConceded).slice(0, 5);
  $: sixerKing = [...playerStatsList].sort((a, b) => b.sixes - a.sixes || a.balls - b.balls).slice(0, 5);
  $: highestScores = [...playerStatsList].sort((a, b) => b.highestScore - a.highestScore).slice(0, 5);
</script>

<div class="screen" style="overflow-y:auto; padding-bottom:60px;">
  <button class="btn btn-ghost" on:click={goBack}>← Back</button>
  
  {#if view === 'list'}
    <div style="display:flex; justify-content:space-between; align-items:center; margin-top:10px;">
      <h1>🏆 Tournaments</h1>
      <button class="btn btn-primary" style="padding:6px 12px; font-size:13px;" on:click={() => view = 'create'}>+ Create</button>
    </div>

    <button class="btn btn-primary btn-block" style="margin-top:12px; padding:12px; font-weight:800; background:linear-gradient(135deg,#059669,#10b981); border:none;" on:click={() => currentScreen.set('cloud-tournaments')}>
      🌐 CLOUD TOURNAMENTS — sab phones par LIVE points table
    </button>

    {#if tournaments.length === 0}
      <div class="card" style="text-align:center; padding: 40px; margin-top:16px;">
        <span style="font-size:48px;">🏆</span>
        <h3 style="margin-top:10px; color:#fff;">No Tournaments Yet</h3>
        <p class="muted" style="font-size:12px;">Create a tournament to schedule round-robin fixtures and maintain an automatic points table.</p>
        <button class="btn btn-primary" style="margin-top:12px;" on:click={() => view = 'create'}>Create New Tournament</button>
      </div>
    {:else}
      {#each tournaments as t, i_each (t.id || t.name || i_each)}
        <div class="card" style="margin-top:12px; display:flex; justify-content:space-between; align-items:center; cursor:pointer;" role="button" tabindex="0" on:click={() => selectTournament(t)} on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectTournament(t)}>
          <div>
            <h3 style="margin:0; color:#fbbf24;">{t.name}</h3>
            <div class="meta" style="font-size:11px; margin-top:4px;">
              🛡️ {t.teams.length} Teams · {t.fixtures.filter(f => f.played).length}/{t.fixtures.length} Played · {t.overs} Overs
            </div>
          </div>
          <div style="display:flex; gap:8px;">
            <button class="btn btn-ghost" style="padding:4px 8px; font-size:12px;" on:click|stopPropagation={() => deleteTournament(t.id)}>🗑</button>
          </div>
        </div>
      {/each}
    {/if}

  {:else if view === 'create'}
    <h1 style="margin-top:10px;">New Tournament</h1>

    <div class="card">
      <div class="field">
        <label for="tour-name">Tournament Name</label>
        <input type="text" id="tour-name" bind:value={name} placeholder="e.g. Summer Premier League" style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;" />
      </div>

      <div class="field" style="margin-top:12px;">
        <label for="tour-overs">Match Overs</label>
        <select id="tour-overs" bind:value={overs} on:change={() => maxOversPerBowler = Math.ceil(overs / 5)} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;">
          {#each [2, 5, 8, 10, 15, 20] as o, i_each (o.id || o.name || i_each)}
            <option value={o}>{o} Overs</option>
          {/each}
        </select>
      </div>

      <!-- Quick Rules -->
      <div class="field" style="margin-top:12px;">
        <label for="tour-max-bowler-overs">Max Bowler Limit</label>
        <input type="number" id="tour-max-bowler-overs" bind:value={maxOversPerBowler} min="1" max={overs} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;" />
      </div>
    </div>

    <!-- PARTICIPATING TEAMS -->
    <fieldset class="card" style="margin-top:12px; border:none; padding:12px;">
      <legend style="margin-top:0; font-size:1.17em; font-weight:bold;">Select Participating Teams</legend>
      <div style="display:flex; flex-direction:column; gap:8px; max-height:240px; overflow-y:auto; padding-right:4px;">
        {#each $availableTeams as t, i_each (t.id || t.name || i_each)}
          <label style="display:flex; align-items:center; gap:10px; background:rgba(0,0,0,0.2); padding:8px; border-radius:6px; cursor:pointer;">
            <input type="checkbox" checked={selectedTeams.includes(t.name)} on:change={() => toggleTeamSelection(t.name)} />
            <img src={t.logo} class="team-logo-sm" style="width:24px; height:24px;" alt="logo" />
            <span style="color:#fff; font-weight:700;">{t.name}</span>
          </label>
        {/each}
      </div>
      <div class="meta" style="margin-top:8px;">{selectedTeams.length} teams selected</div>
    </fieldset>

    <button class="btn btn-primary btn-block" style="margin-top:16px;" on:click={createTournament}>Generate Tournament & Fixtures</button>

  {:else if view === 'detail' && activeTournament}
    <h1 style="margin-top:10px; color:#fbbf24;">{activeTournament.name}</h1>

    <!-- TABS MENU -->
    <div style="display:flex; gap:6px; margin: 12px 0 16px;">
      <button class="btn {detailTab === 'table' ? 'btn-primary' : 'btn-ghost'}" style="flex:1; font-size:11px; padding:6px 0;" on:click={() => detailTab = 'table'}>📊 Table</button>
      <button class="btn {detailTab === 'bracket' ? 'btn-primary' : 'btn-ghost'}" style="flex:1; font-size:11px; padding:6px 0;" on:click={() => detailTab = 'bracket'}>🌿 Playoffs</button>
      <button class="btn {detailTab === 'fixtures' ? 'btn-primary' : 'btn-ghost'}" style="flex:1; font-size:11px; padding:6px 0;" on:click={() => detailTab = 'fixtures'}>📅 Fixtures</button>
      <button class="btn {detailTab === 'stats' ? 'btn-primary' : 'btn-ghost'}" style="flex:1; font-size:11px; padding:6px 0;" on:click={() => detailTab = 'stats'}>🏆 Stats</button>
    </div>

    {#if detailTab === 'table'}
    <!-- POINTS TABLE -->
    <div class="card" style="padding:10px; overflow-x:auto;">
      <h3 style="margin:4px 8px 10px; color:#fff;">Points Table</h3>
      <table class="points-table" style="width:100%; border-collapse:collapse; text-align:left; font-size:13px;">
        <thead>
          <tr style="border-bottom:1px solid rgba(255,255,255,0.1); color:#9ca3af;">
            <th style="padding:6px 8px;">Team</th>
            <th style="padding:6px 8px; text-align:center;">P</th>
            <th style="padding:6px 8px; text-align:center;">W</th>
            <th style="padding:6px 8px; text-align:center;">L</th>
            <th style="padding:6px 8px; text-align:center;">T</th>
            <th style="padding:6px 8px; text-align:center;">NRR</th>
            <th style="padding:6px 8px; text-align:center; color:#fbbf24;">Pts</th>
          </tr>
        </thead>
        <tbody>
          {#each sortedPoints as row, idx (idx)}
            <tr style="border-bottom:1px solid rgba(255,255,255,0.05); color:#fff; font-weight:700; background:{idx < 2 ? 'rgba(16, 185, 129, 0.08)' : 'transparent'};">
              <td style="padding:8px; white-space:nowrap;">
                {#if idx < 2}<span style="color:#10b981; font-size:10px; margin-right:4px;">Q</span>{/if}
                {row.teamName || row.name}
              </td>
              <td style="padding:8px; text-align:center; color:#d1d5db;">{row.matches || row.played || 0}</td>
              <td style="padding:8px; text-align:center; color:#86efac;">{row.won}</td>
              <td style="padding:8px; text-align:center; color:#fca5a5;">{row.lost}</td>
              <td style="padding:8px; text-align:center; color:#9ca3af;">{row.tied}</td>
              <td style="padding:8px; text-align:center; font-family:monospace; color:#cbd5e1;">
                {row.nrr >= 0 ? '+' : ''}{row.nrr.toFixed(3)}
              </td>
              <td style="padding:8px; text-align:center; color:#fbbf24; font-weight:900; font-size:14px;">{row.pts}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    {/if}

    {#if detailTab === 'bracket'}
      <KnockoutBracket fixtures={playoffFixtures} />
    {/if}

  {#if detailTab === 'fixtures'}
    <!-- FIXTURES -->
    <h3 style="margin:20px 8px 10px; color:#fff;">Fixtures & Results</h3>
    {#each activeTournament.fixtures as f, i_each (f.id || f.name || i_each)}
      <div class="card" style="margin-bottom:8px; display:flex; justify-content:space-between; align-items:center; padding:12px;">
        <div style="flex:1;">
          <div style="display:flex; align-items:center; gap:8px;">
            <span style="font-weight:800; color:#fff; font-size:14px;">{f.teamA}</span>
            <span class="muted" style="font-size:11px;">vs</span>
            <span style="font-weight:800; color:#fff; font-size:14px;">{f.teamB}</span>
          </div>
          {#if f.played}
            <div style="color:#86efac; font-size:11px; font-weight:700; margin-top:4px;">
              🏆 {f.winner === 'TIE' ? 'Match Tied' : `${f.winner === 'A' ? f.teamA : f.teamB} won ${f.margin}`}
            </div>
          {:else}
            <div class="muted" style="font-size:11px; margin-top:4px;">Fixture Pending</div>
          {/if}
        </div>
        {#if !f.played}
          <button class="btn btn-primary" style="padding:4px 10px; font-size:12px;" on:click={() => playFixture(f)}>PLAY</button>
        {/if}
      </div>
    {/each}
  {/if}

  {#if detailTab === 'stats'}
    <!-- Orange Cap (Most Runs) -->
    <div class="card" style="margin-top:12px; padding:12px;">
      <h3 style="margin:0 0 10px 0; color:#fbbf24; display:flex; justify-content:space-between; align-items:center; font-size:14px;">
        <span>🟠 ORANGE CAP (Most Runs)</span>
        <span style="font-size:11px; color:#9ca3af; font-weight:normal;">Top 5</span>
      </h3>
      {#if orangeCap.length === 0}
        <div class="muted" style="font-size:12px; text-align:center; padding:10px 0;">No stats recorded yet. Play a tournament match!</div>
      {:else}
        <table style="width:100%; font-size:12px;">
          <thead>
            <tr style="color:#9ca3af; border-bottom:1px solid rgba(255,255,255,0.05);">
              <th style="padding:4px; text-align:left;">Player</th>
              <th style="padding:4px;">Team</th>
              <th style="padding:4px; text-align:center;">M</th>
              <th style="padding:4px; text-align:right; color:#fbbf24;">Runs</th>
            </tr>
          </thead>
          <tbody>
            {#each orangeCap as p, i (i)}
              <tr style="border-bottom:1px solid rgba(255,255,255,0.02); font-weight:700;">
                <td style="padding:6px 4px; color:#fff;">{i+1}. {p.name}</td>
                <td style="padding:6px 4px; color:#9ca3af; font-size:11px;">{p.team}</td>
                <td style="padding:6px 4px; text-align:center; color:#d1d5db;">{p.matches}</td>
                <td style="padding:6px 4px; text-align:right; color:#fbbf24; font-weight:900;">{p.runs} <span style="font-weight:normal; font-size:10px; color:#6b7280;">({p.balls}b)</span></td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>

    <!-- Purple Cap (Most Wickets) -->
    <div class="card" style="margin-top:12px; padding:12px;">
      <h3 style="margin:0 0 10px 0; color:#c084fc; display:flex; justify-content:space-between; align-items:center; font-size:14px;">
        <span>🟣 PURPLE CAP (Most Wickets)</span>
        <span style="font-size:11px; color:#9ca3af; font-weight:normal;">Top 5</span>
      </h3>
      {#if purpleCap.length === 0}
        <div class="muted" style="font-size:12px; text-align:center; padding:10px 0;">No stats recorded yet. Play a tournament match!</div>
      {:else}
        <table style="width:100%; font-size:12px;">
          <thead>
            <tr style="color:#9ca3af; border-bottom:1px solid rgba(255,255,255,0.05);">
              <th style="padding:4px; text-align:left;">Player</th>
              <th style="padding:4px;">Team</th>
              <th style="padding:4px; text-align:center;">M</th>
              <th style="padding:4px; text-align:right; color:#c084fc;">Wkts</th>
            </tr>
          </thead>
          <tbody>
            {#each purpleCap as p, i (i)}
              <tr style="border-bottom:1px solid rgba(255,255,255,0.02); font-weight:700;">
                <td style="padding:6px 4px; color:#fff;">{i+1}. {p.name}</td>
                <td style="padding:6px 4px; color:#9ca3af; font-size:11px;">{p.team}</td>
                <td style="padding:6px 4px; text-align:center; color:#d1d5db;">{p.matches}</td>
                <td style="padding:6px 4px; text-align:right; color:#c084fc; font-weight:900;">{p.wickets} <span style="font-weight:normal; font-size:10px; color:#6b7280;">({p.runsConceded} runs)</span></td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>

    <!-- Sixer King (Most Sixes) -->
    <div class="card" style="margin-top:12px; padding:12px;">
      <h3 style="margin:0 0 10px 0; color:#3b82f6; display:flex; justify-content:space-between; align-items:center; font-size:14px;">
        <span>🔵 SIXER KING (Most Sixes)</span>
        <span style="font-size:11px; color:#9ca3af; font-weight:normal;">Top 5</span>
      </h3>
      {#if sixerKing.length === 0}
        <div class="muted" style="font-size:12px; text-align:center; padding:10px 0;">No stats recorded yet. Play a tournament match!</div>
      {:else}
        <table style="width:100%; font-size:12px;">
          <thead>
            <tr style="color:#9ca3af; border-bottom:1px solid rgba(255,255,255,0.05);">
              <th style="padding:4px; text-align:left;">Player</th>
              <th style="padding:4px;">Team</th>
              <th style="padding:4px; text-align:center;">M</th>
              <th style="padding:4px; text-align:right; color:#3b82f6;">Sixes</th>
            </tr>
          </thead>
          <tbody>
            {#each sixerKing as p, i (i)}
              <tr style="border-bottom:1px solid rgba(255,255,255,0.02); font-weight:700;">
                <td style="padding:6px 4px; color:#fff;">{i+1}. {p.name}</td>
                <td style="padding:6px 4px; color:#9ca3af; font-size:11px;">{p.team}</td>
                <td style="padding:6px 4px; text-align:center; color:#d1d5db;">{p.matches}</td>
                <td style="padding:6px 4px; text-align:right; color:#3b82f6; font-weight:900;">{p.sixes}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>

    <!-- Highest Score -->
    <div class="card" style="margin-top:12px; padding:12px;">
      <h3 style="margin:0 0 10px 0; color:#10b981; display:flex; justify-content:space-between; align-items:center; font-size:14px;">
        <span>🟢 HIGHEST SCORE (Innings Best)</span>
        <span style="font-size:11px; color:#9ca3af; font-weight:normal;">Top 5</span>
      </h3>
      {#if highestScores.length === 0}
        <div class="muted" style="font-size:12px; text-align:center; padding:10px 0;">No stats recorded yet. Play a tournament match!</div>
      {:else}
        <table style="width:100%; font-size:12px;">
          <thead>
            <tr style="color:#9ca3af; border-bottom:1px solid rgba(255,255,255,0.05);">
              <th style="padding:4px; text-align:left;">Player</th>
              <th style="padding:4px;">Team</th>
              <th style="padding:4px; text-align:right; color:#10b981;">Score</th>
            </tr>
          </thead>
          <tbody>
            {#each highestScores as p, i (i)}
              <tr style="border-bottom:1px solid rgba(255,255,255,0.02); font-weight:700;">
                <td style="padding:6px 4px; color:#fff;">{i+1}. {p.name}</td>
                <td style="padding:6px 4px; color:#9ca3af; font-size:11px;">{p.team}</td>
                <td style="padding:6px 4px; text-align:right; color:#10b981; font-weight:900;">{p.highestScore}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>
  {/if}
  {/if}
</div>

<style>
  .points-table th, .points-table td {
    padding: 8px;
  }
  .points-table tr:nth-child(even) {
    background: rgba(255,255,255,0.02);
  }
</style>
